import React, { useEffect } from "react";
import { X } from "lucide-react";

export function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    yellow: "bg-amber-50 text-amber-700 ring-amber-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    red: "bg-rose-50 text-rose-700 ring-rose-100",
    purple: "bg-violet-50 text-violet-700 ring-violet-100",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1", tones[tone] || tones.neutral)}>
      {children}
    </span>
  );
}

export function KpiCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
          {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

export function ModalShell({ title, subtitle, onClose, children }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <button onClick={onClose} className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50" aria-label="Fermer">
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

export function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="h-4 w-44 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
            </div>
            <div className="h-7 w-20 animate-pulse rounded-full bg-slate-100" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="mt-4 space-y-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="mt-5 h-9 w-full animate-pulse rounded-xl bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

export function formatMoneyXOF(n) {
  const value = Number(n || 0);
  return value.toLocaleString("fr-FR", { maximumFractionDigits: 0 }) + " XOF";
}

export function parseMoneyToInt(v) {
  const raw = String(v ?? "").trim();
  if (!raw) return NaN;
  const cleaned = raw.replace(/\s/g, "").replace(",", ".");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n);
}

export function parseTvaPct(value, fallback = 18) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, n));
}

export function splitInstallments(totalInt, count) {
  const base = Math.floor(totalInt / count);
  let remainder = totalInt - base * count;
  const parts = Array.from({ length: count }, () => base);
  for (let i = count - 1; i >= 0 && remainder > 0; i -= 1) {
    parts[i] += 1;
    remainder -= 1;
  }
  return parts;
}

export function addInterval(date, frequency, i) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  if (frequency === "weekly") d.setDate(d.getDate() + 7 * i);
  if (frequency === "biweekly") d.setDate(d.getDate() + 14 * i);
  if (frequency === "monthly") {
    const day = d.getDate();
    d.setMonth(d.getMonth() + i);
    if (d.getDate() !== day) d.setDate(0);
  }
  return d.toISOString().slice(0, 10);
}

export function buildInstallments(totalInt, count, startDate, frequency) {
  const parts = splitInstallments(totalInt, count);
  return parts.map((amount, i) => ({
    amount,
    due_date: addInterval(startDate, frequency, i),
  }));
}

export function clampDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export function parseProviderFromNotes(notes) {
  const s = String(notes || "");
  const m = s.match(/^\[provider:([^\]]+)\]\s*/i);
  if (!m) return { provider: null, cleanNotes: s };
  return {
    provider: String(m[1] || "").trim() || null,
    cleanNotes: s.replace(/^\[provider:[^\]]+\]\s*/i, ""),
  };
}
